import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import {
  GUID,
  IElement,
  IElementBase,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { IElementsRecord } from "@faro-lotv/project-source";

/**
 * @returns true if this element should be hidden if empty
 * @param element the element to check
 */
export function shouldHideIfEmpty(
  element: LightweightElement | IElement,
): boolean {
  return (
    element.type === IElementType.group ||
    element.type === IElementType.timeSeries
  );
}

/**
 * This functions check for nodes that should not be visible in the project tree
 *
 * @param element to check
 * @param elements in the project
 * @param videoModeCopyTagId id of the tag identifying rooms copied for video mode datasets
 * @returns true if this element is a dataset to hide
 */
export function isElementNodeToHide(
  element: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
  videoModeCopyTagId?: GUID,
): boolean {
  // Hide the pointcloud analysis elements from the project tree
  if (
    element.type === IElementType.section &&
    element.typeHint === IElementTypeHint.analysis
  ) {
    return true;
  }

  if (
    element.type === IElementType.section &&
    element.typeHint === IElementTypeHint.captureTree
  ) {
    return true;
  }

  // Hide time points that do not have 360s: this will happen in case of broken projects (it should
  // be rare) or if the time point contains only cube maps (that the viewer does not support)
  // REMINDER: if anything breaks in the viewer due to these elements, let's check if we consolidate
  // the logic to just drop these kind of elements.
  const parent = element.parentId ? elements[element.parentId] : undefined;
  if (
    element.type === IElementType.section &&
    (element.typeHint === IElementTypeHint.room ||
      element.typeHint === IElementTypeHint.timeTravel) &&
    parent?.type === IElementType.timeSeries
  ) {
    return !element.childrenIds?.some(
      (c) => elements[c]?.type === IElementType.img360,
    );
  }

  // Hide rooms that are direct children of the slide container, as they are in the staging state in WebEditor
  // and do not have any area assigned to them.
  // For more context, see - https://faro01.atlassian.net/browse/SWEB-8297
  if (
    element.type === IElementType.section &&
    element.typeHint === IElementTypeHint.room &&
    parent?.type === IElementType.group &&
    parent.typeHint === IElementTypeHint.slideContainer
  ) {
    return true;
  }

  if (!videoModeCopyTagId) return false;

  // Hide a room only if it contains only time points from videomode trajectories
  if (
    element.type === IElementType.section &&
    element.typeHint === IElementTypeHint.room &&
    element.childrenIds?.length
  ) {
    const childElement = elements[element.childrenIds[0]];
    if (childElement?.childrenIds?.length) {
      if (
        childElement.childrenIds.every((grandChildId) => {
          const grandChild = elements[grandChildId];
          return (
            grandChild &&
            !!grandChild.labelIds?.some((label) => label === videoModeCopyTagId)
          );
        })
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @returns true if the typeHint is a videoRecording or an odometryPath
 * @param typeHint it's the typeHint of the iElement to check
 */
export function isVideoRecordingOrOdometryPath(
  typeHint: IElementBase["typeHint"],
): boolean {
  return (
    typeHint === IElementTypeHint.videoRecordings ||
    typeHint === IElementTypeHint.videoRecordingTrack ||
    typeHint === IElementTypeHint.odometryPath
  );
}
